// Public surface: list of transforms + a builder that turns
// (Song, EditOps) into a Command[] for the CommandPalette. Voices for
// which a transform errors are filtered out so the palette only shows
// commands the user can actually run.

import type { Command } from '../components/CommandPalette';
import type { EditOps } from '../blocks/editContext';
import type { Song } from '../blocks/types';

import { humanizeTransform } from './humanize';
import { reverseTransform } from './reverse';
import { shuffleTransform } from './shuffle';
import {
  transposeDownOctaveTransform,
  transposeUpOctaveTransform,
} from './transposeOctave';
import { namedVoices, type Transform } from './types';

export type { Transform, TransformContext, TransformResult } from './types';
export { namedVoices } from './types';

// Stable ordering — palette readability beats alphabetical.
export const ALL_TRANSFORMS: Transform[] = [
  humanizeTransform,
  shuffleTransform,
  reverseTransform,
  transposeUpOctaveTransform,
  transposeDownOctaveTransform,
];

// Build one Command per (transform × applicable voice). Each command,
// when run, invokes the transform with a fresh seed and applies the
// resulting recipe via songEdit ops.
export function buildTransformCommands(
  song: Song | null,
  ops: EditOps,
): Command[] {
  if (!song) return [];
  const voices = namedVoices(song);
  if (voices.length === 0) return [];

  const out: Command[] = [];
  for (const transform of ALL_TRANSFORMS) {
    for (const { name } of voices) {
      // Probe the transform with a deterministic seed; if it errors
      // for this voice (wrong kind, no structure), skip it. The
      // production seed at run-time is fresh — see below.
      const probe = transform.apply({ song, voiceName: name, seed: 0 });
      if (!probe.ok) continue;
      out.push({
        id: `transform.${transform.id}.${name}`,
        label: `${transform.verb} → ${name}`,
        description: transform.description,
        section: `Transforms · ${capitalize(transform.category)}`,
        keywords: [transform.id, name, transform.category],
        run: () => {
          const seed = freshSeed();
          const result = transform.apply({ song, voiceName: name, seed });
          if (!result.ok) return;
          ops.update(result.recipe);
        },
      });
    }
  }
  return out;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

// Fresh seed per run — cheap, doesn't need to be cryptographic.
function freshSeed(): number {
  return ((Date.now() & 0xffffffff) ^ (Math.random() * 0x100000000)) | 0;
}
